import { Component, inject, input, signal } from '@angular/core';
import {
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonCol,
  IonGrid,
  IonRow,
  ModalController,
  NavController
} from '@ionic/angular/standalone';
import { CustomModalComponent } from '@shared/components/modals/custom-modal/custom-modal.component';
import { ActionButtonTypes } from '@shared/components/modals/custom-modal/models/action-button.enum';
import { ConfirmationModalComponent } from '@shared/components/modals/confirmation-modal/confirmation-modal.component';
import type { ActionButton } from '@shared/components/modals/custom-modal/models/action-button.models';
import { AuthService } from '@core/auth/services/auth.service';
import { PersonHttpService } from '@features/person/services/person-http.service';
import { LoadingService } from '@core/services/loading.service';
import { take } from 'rxjs';

@Component({
  selector: 'app-cookie-policy',
  templateUrl: './cookie-policy.component.html',
  styleUrls: ['./cookie-policy.component.scss'],
  standalone: true,
  imports: [
    IonCard,
    IonCardContent,
    IonCardHeader,
    IonCardTitle,
    IonCol,
    IonGrid,
    IonRow,
    CustomModalComponent
  ]
})
export class CookiePolicyComponent {
  private modalController = inject(ModalController);
  private authService = inject(AuthService);
  private navCtrl = inject(NavController);
  private personHttpService = inject(PersonHttpService);
  private loadingService = inject(LoadingService);

  title = 'Technically Necessary Cookies '.toUpperCase();

  actionButtons = signal<ActionButton[]>([
    { text: 'Disagree', action: ActionButtonTypes.decline, color: 'secondary' }
  ]);
  hideActionButtons = input(false);

  handleBack(): void {
    this.modalController.dismiss({ dismissed: 'back' }).then();
  }

  async handleAction(action: string): Promise<void> {
    if (action === ActionButtonTypes.decline) {
      await this.openDeclineConfirmationModal();
    } else if (action === ActionButtonTypes.accept) {
      // Handle accept action
      this.modalController.dismiss({ action: 'accepted' });
    }
  }

  async openDeclineConfirmationModal(): Promise<void> {
    const modal = await this.modalController.create({
      component: ConfirmationModalComponent,
      componentProps: {
        title: signal('Decline Technically Necessary Cookies'),
        content:
          signal(`<p>By declining the technically necessary cookies, you will not be able to use this application.</p>
                  <p>You will be logged out and returned to the login screen.</p>
                  <p>Are you sure you want to continue?</p>`),
        yesText: signal('Yes, Decline'),
        noText: signal('Cancel'),
        showCloseButton: signal(false)
      },
      backdropDismiss: false,
      cssClass: 'centered-dialog-modal'
    });

    await modal.present();

    const { data } = await modal.onDidDismiss();

    if (data) {
      this.loadingService.startLoading();
      this.personHttpService
        .revokePrivacyPolicyDate()
        .pipe(take(1))
        .subscribe(() => {
          this.modalController
            .dismiss({ action: 'declined_confirmed' })
            .then(() => {
              this.loadingService.stopLoading();
              this.goToLogout();
            })
            .catch(() => {
              this.loadingService.stopLoading();
              this.goToLogout();
            });
        });
    }
  }

  private async goToLogout() {
    await this.authService.logout();
    this.navCtrl.navigateRoot('/auth').then();
  }
}
